import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  useTheme,
  Fade
} from '@mui/material';
import {
  ArrowRight,
  PenTool,
  Palette,
  Home,
  Lightbulb,
  Ruler,
  Sofa
} from 'lucide-react';

// Editorial Feature Card Component - Reusable Pattern
const FeatureCard = ({ icon: Icon, title, description, delay }) => {
  return (
    <Fade in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Card 
        sx={{ 
          height: '100%', 
          textAlign: 'center',
          background: '#FFFFFF',
          border: '1px solid #E0E0E0',
          borderRadius: 0,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            borderColor: '#FF5722'
          }
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              color: 'white',
              boxShadow: '0 8px 25px rgba(255,87,34,0.3)'
            }}
          >
            <Icon size={32} />
          </Box>
          
          <Typography 
            variant="h5" 
            component="h3" 
            color="#1A1A1A" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              mb: 2,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {title}
          </Typography>
          
          <Typography 
            variant="body1" 
            color="#666" 
            sx={{ 
              lineHeight: 1.6,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>
    </Fade>
  );
};

const InteriorDesignPage = () => {
  const [showElements, setShowElements] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowElements(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: PenTool,
      title: 'Concept Development',
      description: 'Creative design concepts that reflect your personality and lifestyle preferences.'
    },
    {
      icon: Palette,
      title: 'Color & Materials',
      description: 'Expert selection of colors, textures, and materials that create harmony.'
    },
    {
      icon: Home,
      title: 'Space Planning',
      description: 'Optimal layout design for functionality and aesthetic appeal.'
    },
    {
      icon: Lightbulb,
      title: 'Lighting Design',
      description: 'Strategic lighting solutions that enhance ambiance and functionality.'
    },
    {
      icon: Ruler,
      title: 'Custom Solutions',
      description: 'Bespoke design elements tailored to your specific needs and space.'
    },
    {
      icon: Sofa,
      title: 'Furniture Selection',
      description: 'Curated furniture pieces that complement your design vision.'
    }
  ];

  const styles = [
    {
      title: 'Modern Contemporary',
      description: 'Clean lines, minimalist aesthetics, and functional beauty.',
      image: '/images/style-modern.jpg'
    },
    {
      title: 'Classic Traditional',
      description: 'Timeless elegance with rich textures and warm colors.',
      image: '/images/style-traditional.jpg'
    },
    {
      title: 'Scandinavian',
      description: 'Light, airy spaces with natural materials and cozy comfort.',
      image: '/images/style-scandinavian.jpg'
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden', background: '#FAFAFA' }}>
      {/* Hero Section - Editorial Pattern */}
      <Box 
        sx={{ 
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #FFFFFF 100%)',
          pt: { xs: 15, md: 20 },
          pb: { xs: 10, md: 15 },
          overflow: 'hidden'
        }}
      >
        {/* Background Geometric Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255,87,34,0.03), transparent)',
            filter: 'blur(60px)',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: 150,
            height: 150,
            background: 'linear-gradient(45deg, rgba(255,87,34,0.02), transparent)',
            transform: 'rotate(45deg)',
            zIndex: 0
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={showElements} timeout={1000}>
                <Box>
                  {/* Badge Component */}
                  <Box 
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 3, 
                      py: 1, 
                      background: 'rgba(255,87,34,0.08)',
                      border: '1px solid rgba(255,87,34,0.2)',
                      borderRadius: 0,
                      mb: 4
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#FF5722"
                      sx={{
                        fontWeight: 600,
                        letterSpacing: 2,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Interior Design
                    </Typography>
                  </Box>
                  
                  {/* Headline */}
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    color="#1A1A1A"
                    sx={{ 
                      fontSize: { xs: '3rem', md: '4.5rem', lg: '5.5rem' },
                      fontWeight: 800,
                      lineHeight: 0.9,
                      mb: 3,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Beautiful{' '}
                    <Box 
                      component="span" 
                      sx={{ 
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Interior
                    </Box>
                    <Box component="span" display="block" mt={1}>
                      Spaces
                    </Box>
                  </Typography>
                  
                  {/* Subtitle */}
                  <Typography 
                    variant="h6" 
                    component="p" 
                    color="#666"
                    sx={{ 
                      mb: 8,
                      lineHeight: 1.7,
                      fontSize: '1.2rem',
                      position: 'relative',
                      pl: 4,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 8,
                        width: 3,
                        height: 40,
                        background: 'linear-gradient(to bottom, #FF5722, #FF8A65)'
                      }
                    }}
                  >
                    Transform your home into a reflection of your style with our 
                    comprehensive interior design services.
                  </Typography>

                  {/* CTA Buttons */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Button
                      component={RouterLink}
                      to="/contact"
                      variant="contained"
                      size="large"
                      endIcon={<ArrowRight />}
                      sx={{
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        color: 'white',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        boxShadow: '0 8px 25px rgba(255,87,34,0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 35px rgba(255,87,34,0.4)'
                        }
                      }}
                    >
                      Start Your Design
                    </Button>
                    
                    <Button
                      component={RouterLink}
                      to="/portfolio"
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: '#1A1A1A',
                        color: '#1A1A1A',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        letterSpacing: 1,
                        borderWidth: 2,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#FF5722',
                          color: '#FF5722',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 20px rgba(255,87,34,0.2)'
                        }
                      }}
                    >
                      View Portfolio
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Fade in={showElements} timeout={1200}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 500,
                      background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/interior-hero.jpg"
                      alt="Interior Design"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'brightness(0.9)'
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 30,
                        left: 30,
                        right: 30,
                        background: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(10px)',
                        p: 3,
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        color="#1A1A1A" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 600,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        Modern Living Room
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="#666"
                        sx={{ 
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        Luxury Apartment - Complete Renovation
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          background: '#FFFFFF'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              color="#999" 
              sx={{ 
                mb: 2, 
                display: 'block',
                fontWeight: 500,
                letterSpacing: 3,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              What We Offer
            </Typography>
            <Typography 
              variant="h2" 
              component="h2" 
              color="#1A1A1A" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Interior Design Services
            </Typography>
            <Typography 
              variant="h6" 
              component="p" 
              color="#666" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                fontWeight: 400,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Complete interior design solutions from concept to completion.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <FeatureCard {...feature} delay={200 * (index + 1)} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Design Styles Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          background: '#F8F8F8'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              color="#999" 
              sx={{ 
                mb: 2, 
                display: 'block',
                fontWeight: 500,
                letterSpacing: 3,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Our Expertise
            </Typography>
            <Typography 
              variant="h2" 
              component="h2" 
              color="#1A1A1A" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Design Styles
            </Typography>
            <Typography 
              variant="h6" 
              component="p" 
              color="#666" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                fontWeight: 400,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              From modern minimalism to classic elegance, we create designs that reflect your unique style.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {styles.map((style, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in={showElements} style={{ transitionDelay: `${400 * (index + 1)}ms` }}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      background: '#FFFFFF',
                      border: '1px solid #E0E0E0',
                      borderRadius: 0,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                        borderColor: '#FF5722'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        height: 250,
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        component="img"
                        src={style.image}
                        alt={style.title}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          filter: 'brightness(0.9)',
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                          height: '50%'
                        }}
                      />
                    </Box>
                    <CardContent sx={{ p: 3 }}>
                      <Typography 
                        variant="h5" 
                        component="h3" 
                        color="#1A1A1A" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 600,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {style.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        color="#666"
                        sx={{ 
                          lineHeight: 1.6,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {style.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Process Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          background: '#FFFFFF'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="overline" 
                color="#999" 
                sx={{ 
                  mb: 2, 
                  display: 'block',
                  fontWeight: 500,
                  letterSpacing: 3,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Our Process
              </Typography>
              <Typography 
                variant="h2" 
                component="h2" 
                color="#1A1A1A" 
                sx={{ 
                  mb: 4,
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                How We Design
              </Typography>
              <Typography 
                variant="body1" 
                color="#666" 
                paragraph
                sx={{ 
                  lineHeight: 1.6,
                  fontSize: '1.1rem',
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Our design process begins with understanding your lifestyle, preferences, and functional needs. 
                We believe that great interior design should not only look beautiful but also enhance how you 
                live and work in your space.
              </Typography>
              <Typography 
                variant="body1" 
                color="#666" 
                paragraph
                sx={{ 
                  lineHeight: 1.6,
                  fontSize: '1.1rem',
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                From initial concept sketches to final styling, we guide you through every step of the design 
                journey, ensuring the result exceeds your expectations and reflects your unique personality.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {[
                  { step: '01', title: 'Discovery', desc: 'Understanding your style, needs, and vision' },
                  { step: '02', title: 'Design', desc: 'Creating concepts and detailed design plans' },
                  { step: '03', title: 'Selection', desc: 'Choosing materials, furniture, and finishes' },
                  { step: '04', title: 'Installation', desc: 'Professional project management and styling' }
                ].map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        boxShadow: '0 8px 25px rgba(255,87,34,0.3)'
                      }}
                    >
                      {item.step}
                    </Box>
                    <Box>
                      <Typography 
                        variant="h6" 
                        color="#1A1A1A" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 600,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        color="#666"
                        sx={{ 
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
          color: 'white'
        }}
      >
        <Container maxWidth="md">
          <Fade in={showElements}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h2" 
                component="h2" 
                color="white" 
                sx={{ 
                  mb: 3,
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Ready to Transform Your Space?
              </Typography>
              <Typography 
                variant="h5" 
                component="p" 
                color="white" 
                sx={{ 
                  mb: 6, 
                  opacity: 0.9,
                  fontWeight: 400,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Let's create beautiful interiors that reflect your unique style and enhance your lifestyle.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowRight />}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 1,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    '&:hover': { 
                      background: 'rgba(255,255,255,0.3)'
                    }
                  }}
                >
                  Start Your Design
                </Button>
                
                <Button
                  component={RouterLink}
                  to="/portfolio"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 1,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    '&:hover': {
                      borderColor: 'white',
                      background: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  View Our Work
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default InteriorDesignPage;